import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getPrescription, updatePrescriptionStatus, updatePrescriptionItem } from '../../api/orders.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import CrossFacilityErrorCard from '../../components/CrossFacilityErrorCard.jsx'
import { formatDateTime, statusColor } from '../../utils/format.js'
import { PRESCRIPTION_TRANSITIONS } from '../../constants/index.js'
import { searchDrugs } from '../../api/catalogues.js'
import CatalogueAutocomplete from '../doctor/CatalogueAutocomplete.jsx'

/**
 * Pharmacy prescription detail.
 *
 * Lists prescription items and offers status transitions: "Dispense" or
 * "Cancel" while the prescription is active. Notes entered here are sent
 * as the `notes` field so the ordering clinician and patient see them.
 */
export default function PharmacyOrderDetail() {
  const { orderId } = useParams()
  const qc = useQueryClient()
  const [notes, setNotes] = useState('')

  const q = useQuery({
    queryKey: ['prescription', orderId],
    queryFn: () => getPrescription(orderId),
    enabled: Boolean(orderId),
  })
  const rx = q.data

  const mut = useMutation({
    mutationFn: ({ next, notes }) => updatePrescriptionStatus(orderId, next, notes),
    onSuccess: (fresh) => {
      qc.setQueryData(['prescription', orderId], fresh)
      qc.invalidateQueries({ queryKey: ['pharmacy-queue'] })
      setNotes('')
    },
  })

  // Tab state — declared BEFORE early returns (Rules of Hooks).
  // Default tab is "items" because that's where dispensing happens.
  const [tab, setTab] = useState('items')

  if (q.isLoading) return <div className="p-4 text-sm text-slate-400">Loading…</div>
  if (q.isError || !rx) {
    // Cross-pharmacy 403 → CrossFacilityErrorCard renders the tel:
    // call screen so the dispenser can phone the correct pharmacy.
    const env = q.error?.response?.data?.error
    if (env?.code === 'cross_pharmacy_access') {
      return (
        <div className="space-y-3">
          <CrossFacilityErrorCard
            error={q.error}
            backTo="/pharmacy"
            backLabel="Back to queue"
          />
        </div>
      )
    }
    return (
      <div className="space-y-3">
        <ErrorBanner error={q.error || new Error('Prescription not found')} onRetry={() => q.refetch()} />
        <Link to="/pharmacy" className="text-xs underline">Back to queue</Link>
      </div>
    )
  }

  const nexts = PRESCRIPTION_TRANSITIONS[(rx.status || '').toLowerCase()] || []
  const items = rx.items && rx.items.length
    ? rx.items
    : (rx.medication_name
        ? [{ id: 'legacy', drug_name: rx.medication_name, dose: rx.dosage, frequency: rx.frequency, duration: rx.duration }]
        : [])

  // Patient identity is prominent at the top — dispensing safety depends on
  // the pharmacist being able to see the recipient at a glance, alongside
  // DOB / sex / phone for callback and identity checks.
  const ageYears = (() => {
    if (!rx.patient_dob) return null
    const d = new Date(rx.patient_dob)
    if (isNaN(d.getTime())) return null
    const now = new Date()
    let a = now.getFullYear() - d.getFullYear()
    const m = now.getMonth() - d.getMonth()
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a -= 1
    return a
  })()

  return (
    <div className="space-y-4">
      {/* Patient banner — prominent so the pharmacist confirms the recipient
          before anything else. */}
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-slate-400">
              Patient
            </div>
            <div className="text-xl font-semibold text-slate-800 break-words">
              {rx.patient_name || 'Unknown patient'}
            </div>
            <div className="mt-1 text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-1">
              {rx.patient_sex && <span className="capitalize">{rx.patient_sex}</span>}
              {rx.patient_dob && (
                <span>
                  DOB {rx.patient_dob}
                  {ageYears !== null && ` · ${ageYears} y`}
                </span>
              )}
              {rx.patient_phone && <span>☎ {rx.patient_phone}</span>}
            </div>
          </div>
          <Link to="/pharmacy" className="text-xs underline">Back</Link>
        </div>
      </Card>

      {/* Prescription metadata */}
      <Card>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-400">Prescription</div>
          <div className="font-semibold">#{rx.id}</div>
          <div className="text-xs text-slate-500 flex flex-wrap items-center gap-2 mt-0.5">
            <span className={`uppercase border rounded-full px-2 py-0.5 text-[10px] ${statusColor(rx.status)}`}>{rx.status}</span>
            <span>Ordered {formatDateTime(rx.created_at)}</span>
            {rx.pharmacy_name && <span>· {rx.pharmacy_name}</span>}
          </div>
        </div>
      </Card>

      {/* ─── Tab strip — Items / Workflow ───────────────────────────
          Patient + Order metadata cards above stay always-visible.
          The Items card holds the per-line dispensing controls; the
          Workflow card holds the parent-order admin transitions
          (Accept / Reject / etc.). Workflow tab auto-hides when there
          are no available admin transitions. */}
      {(() => {
        const TABS = [
          { id: 'items',    label: 'Items' },
          { id: 'workflow', label: 'Workflow', hidden: nexts.length === 0 },
        ].filter((t) => !t.hidden)
        return (
          <div className="border-b-2 border-slate-200 flex flex-wrap gap-2">
            {TABS.map((t) => {
              const active = tab === t.id
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  className={
                    `px-6 py-3 text-base tracking-wide border-b-[3px] -mb-[2px] transition-colors ` +
                    (active
                      ? 'border-wantok-700 text-wantok-800 font-bold bg-wantok-50/60'
                      : 'border-transparent text-slate-600 font-semibold hover:text-wantok-800 hover:bg-slate-50')
                  }
                >
                  {t.label}
                </button>
              )
            })}
          </div>
        )
      })()}

      {tab === 'items' && (
        <Card>
          <CardHeader title="Items" />
          <ul className="divide-y divide-slate-100">
            {items.length === 0 && (
              <li className="py-3 text-sm text-slate-400">No items on this prescription.</li>
            )}
            {items.map((it) => (
              <PharmacyItemRow
                key={it.id}
                item={it}
                onSaved={(fresh) => {
                  qc.invalidateQueries({ queryKey: ['prescription', orderId] })
                  qc.invalidateQueries({ queryKey: ['pharmacy-queue'] })
                }}
              />
            ))}
          </ul>
          {rx.notes && (
            <div className="mt-3 text-xs text-slate-500">
              <div className="uppercase tracking-wide text-[10px] text-slate-400">Clinician notes</div>
              <div className="mt-1 whitespace-pre-wrap">{rx.notes}</div>
            </div>
          )}
        </Card>
      )}

      {tab === 'workflow' && nexts.length > 0 && (
        <Card>
          <CardHeader title="Actions" subtitle="Update status visible to the clinician and patient" />
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional dispense notes"
            className="w-full border rounded px-2 py-1.5 text-sm"
          />
          <div className="mt-2 flex flex-wrap gap-2">
            {nexts.map(({ label, next }) => (
              <button
                key={next}
                onClick={() => mut.mutate({ next, notes })}
                disabled={mut.isPending}
                className="text-sm bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2 disabled:opacity-60"
              >
                {label}
              </button>
            ))}
          </div>
          {mut.isError && <div className="mt-2"><ErrorBanner error={mut.error} /></div>}
        </Card>
      )}
    </div>
  )
}


// ---------------------------------------------------------------------------
// PharmacyItemRow — one prescribed drug + the pharmacy's per-item
// dispensing form. Status select drives which extra fields appear:
//   dispensed             → just save
//   partially_dispensed   → dispensed_quantity input
//   substituted           → substituted_drug_name input
//   not_available         → unavailable_reason input
// pharmacist_comment is always offered.
// ---------------------------------------------------------------------------

const STATUS_OPTIONS = [
  { v: 'pending',             label: 'Pending',     tone: 'bg-slate-100 text-slate-700' },
  { v: 'dispensed',           label: 'Dispensed',   tone: 'bg-emerald-100 text-emerald-800' },
  { v: 'partially_dispensed', label: 'Partial',     tone: 'bg-amber-100 text-amber-800' },
  { v: 'substituted',         label: 'Substituted', tone: 'bg-violet-100 text-violet-800' },
  { v: 'not_available',       label: 'Not avail.',  tone: 'bg-rose-100 text-rose-800' },
]

function PharmacyItemRow({ item, onSaved }) {
  const [status, setStatus] = useState(item.dispensing_status || 'pending')
  const [qty, setQty] = useState(item.dispensed_quantity || '')
  const [sub, setSub] = useState(item.substituted_drug_name || '')
  const [subCode, setSubCode] = useState(item.substituted_drug_code || '')
  const [reason, setReason] = useState(item.unavailable_reason || '')
  const [note, setNote] = useState(item.pharmacist_comment || '')
  const [savedFlash, setSavedFlash] = useState(false)

  // Single-unit count actually handed out - the ONLY value that moves
  // pharmacy stock. Shown for dispensed / partial; defaults to the
  // prescribed unit count when present.
  const [units, setUnits] = useState(
    item.dispensed_units ?? item.quantity_units ?? ''
  )
  // Does this status hand drug out (and therefore touch stock)?
  const deducts = status === 'dispensed' || status === 'partially_dispensed' || status === 'substituted'
  const stockOnHand = item.stock_on_hand  // null when not formulary-linked

  const mut = useMutation({
    mutationFn: () => updatePrescriptionItem(item.id, {
      dispensing_status: status,
      dispensed_quantity: status === 'partially_dispensed' ? qty : '',
      // Numeric units drive stock. Send a number when dispensing, else
      // null so a reverted status returns the stock.
      dispensed_units: deducts && units !== '' ? Number(units) : null,
      substituted_drug_name: status === 'substituted' ? sub : '',
      substituted_drug_code: status === 'substituted' ? subCode : '',
      unavailable_reason: status === 'not_available' ? reason : '',
      pharmacist_comment: note,
    }),
    onSuccess: (fresh) => {
      setSavedFlash(true)
      setTimeout(() => setSavedFlash(false), 2500)
      onSaved?.(fresh)
    },
  })

  const tone = STATUS_OPTIONS.find((o) => o.v === status)?.tone || STATUS_OPTIONS[0].tone

  return (
    <li className="py-3 border-b border-slate-100 last:border-b-0">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-slate-900">{item.drug_name}</div>
          <div className="text-[11px] text-slate-500 flex flex-wrap gap-3 mt-0.5">
            {item.dose && <span>Dose: {item.dose}</span>}
            {item.frequency && <span>Frequency: {item.frequency}</span>}
            {item.duration && <span>Duration: {item.duration}</span>}
            {item.quantity && <span>Qty: {item.quantity}</span>}
            {typeof stockOnHand === 'number' && (
              <span className={stockOnHand > 0 ? 'text-emerald-700' : 'text-rose-700'}>
                Stock on hand: {stockOnHand}
              </span>
            )}
            {item.stock_status === 'out' && (
              <span className="text-[10px] font-bold uppercase rounded-full px-1.5 py-0.5 bg-rose-100 text-rose-800">Out</span>
            )}
            {item.stock_status === 'low' && (
              <span className="text-[10px] font-bold uppercase rounded-full px-1.5 py-0.5 bg-amber-100 text-amber-900">Low</span>
            )}
          </div>
          {item.instructions && (
            <div className="text-xs text-slate-500 mt-1 italic">{item.instructions}</div>
          )}
        </div>
        <span className={`text-[10px] font-bold uppercase tracking-wide ${tone} rounded-full px-2 py-0.5 shrink-0`}>
          {STATUS_OPTIONS.find((o) => o.v === status)?.label}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">Status</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.v} value={o.v}>{o.label}</option>
            ))}
          </select>
        </label>

        {deducts && (
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">
              Units dispensed (single tablets / items)
            </span>
            <input
              type="number"
              min="0"
              step="1"
              value={units}
              onChange={(e) => setUnits(e.target.value)}
              placeholder={item.quantity_units != null ? `Prescribed: ${item.quantity_units}` : 'e.g. 21'}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
            />
            <span className="mt-0.5 block text-[10px] text-slate-400">
              Subtracted from pharmacy stock when you save.
            </span>
          </label>
        )}

        {status === 'partially_dispensed' && (
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">Dispensed quantity (note)</span>
            <input
              type="text"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              placeholder="e.g. 10 of 21 tablets"
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
            />
          </label>
        )}

        {status === 'substituted' && (
          <label className="block sm:col-span-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">Substituted with</span>
            {sub ? (
              <div className="mt-1 flex items-center gap-2 text-sm">
                <span className="font-medium text-slate-800">{sub}</span>
                {subCode && <span className="font-mono text-[10px] text-slate-400">#{subCode}</span>}
                <button
                  type="button"
                  onClick={() => { setSub(''); setSubCode('') }}
                  className="text-[11px] text-rose-600 underline"
                >
                  change
                </button>
              </div>
            ) : (
              <div className="mt-1">
                <CatalogueAutocomplete
                  queryKey="pharmacy-substitute-drugs"
                  searchFn={searchDrugs}
                  placeholder="Search the substitute drug…"
                  rowKey={(d) => d.code}
                  allowFreeText
                  freeTextLabel="Use typed name (not in formulary — no stock change)"
                  renderRow={(d) => (
                    <>
                      <span className="font-mono text-xs text-wantok-700">{d.code}</span>
                      <span className="ml-2">{d.name}</span>
                      {d.strength && <span className="text-slate-400 ml-2 text-xs">{d.strength}</span>}
                    </>
                  )}
                  onPick={(d) => { setSub(d.name || ''); setSubCode(d.code || '') }}
                />
              </div>
            )}
            <span className="mt-0.5 block text-[10px] text-slate-400">
              Stock is taken from the substitute drug when you save (formulary picks only).
            </span>
          </label>
        )}

        {status === 'not_available' && (
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">Unavailable reason</span>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Out of stock; expected back next week"
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
            />
          </label>
        )}

        <label className="block sm:col-span-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">Pharmacist comment (optional)</span>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Counselled on side-effects, take with food."
            className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
          />
        </label>
      </div>

      <div className="mt-2 flex items-center justify-end gap-3">
        {mut.isPending && <span className="text-xs text-slate-500">Saving…</span>}
        {!mut.isPending && savedFlash && <span className="text-xs font-semibold text-emerald-700">Saved ✓</span>}
        <button
          onClick={() => mut.mutate()}
          disabled={mut.isPending}
          className="text-xs font-semibold bg-wantok-700 hover:bg-wantok-800 disabled:bg-slate-300 text-white rounded px-3 py-1.5"
        >
          {mut.isPending ? 'Saving…' : 'Save item'}
        </button>
      </div>
      {mut.isError && (
        <div className="mt-2 text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded px-2 py-1">
          {mut.error?.response?.data?.detail || mut.error?.message || 'Failed to save.'}
        </div>
      )}
    </li>
  )
}
